/** Bill wise release (screen 190): receipt row + invoice grid helpers. */

import {
  parseAllocationNumber,
  roundMoney,
} from "./billAllocationUtils";

export const BILL_WISE_RELEASE_INVOICE_BIND = "bill_wise_release_invoice_split";
export const BILL_WISE_RELEASE_INVOICE_BASE_KEY =
  "_bill_wise_release_invoice_base";
export const BILL_WISE_RELEASE_RECEIPT_BIND =
  "bill_wise_release_unregistered_split";
export const VENDOR_BILL_WISE_RELEASE_PURCHASE_BIND =
  "vendor_bill_wise_release_purchase_split";
export const VENDOR_BILL_WISE_RELEASE_PURCHASE_BASE_KEY =
  "_vendor_bill_wise_release_purchase_base";
export const VENDOR_BILL_WISE_RELEASE_RECEIPT_BIND =
  "vendor_bill_wise_release_unregistered_split";
export const BILL_WISE_RELEASE_INVOICE_FIELD = "drp_invoice";
export const VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD = "drp_purchase";
export const BILL_WISE_RELEASE_ALLOCATE_FIELD = "txt_allocate_amount";
export const BILL_WISE_RELEASE_RELEASE_FIELD = "txt_release_amount";

export const resolveInvoiceRawItem = (
  row: Record<string, any>,
  invoiceField: string = BILL_WISE_RELEASE_INVOICE_FIELD
): Record<string, any> | null => {
  const key = `_${invoiceField}_selectedOption`;
  const selected = row[key];
  if (selected && typeof selected === "object") {
    return selected as Record<string, any>;
  }
  return null;
};

export const getInvoiceNoFromRow = (row: Record<string, any>): string =>
  String(
    row.txt_purchase_no ??
      row.purchase_no ??
      row.txt_invoice_no ??
      row.invoice_no ??
      resolveInvoiceRawItem(row, VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD)
        ?.purchase_no ??
      resolveInvoiceRawItem(row)?.invoice_no ??
      ""
  ).trim();

export const getInvoiceIdFromRow = (row: Record<string, any>): string =>
  String(
    row[VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD] ??
      row.drp_purchase ??
      row.purchase_id ??
      row[BILL_WISE_RELEASE_INVOICE_FIELD] ??
      row.invoice_id ??
      resolveInvoiceRawItem(row, VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD)?.id ??
      resolveInvoiceRawItem(row, VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD)
        ?.value ??
      resolveInvoiceRawItem(row)?.id ??
      resolveInvoiceRawItem(row)?.value ??
      ""
  ).trim();

/** Display / current paid on invoice row (may be preview-adjusted). */
export const getRowPaidAmount = (row: Record<string, any>): number =>
  parseAllocationNumber(
    row.txt_amount_paid ??
      row.amount_paid ??
      row.txt_paid_amount ??
      0
  );

/** Original paid from lookup (never preview-depleted). */
export const getInvoiceOriginalPaidAmount = (row: Record<string, any>): number =>
  parseAllocationNumber(
    row.amount_paid_original ??
      row.txt_amount_paid_original ??
      row.txt_amount_paid ??
      row.amount_paid ??
      0
  );

/** Original due from lookup (never preview-increased). */
export const getInvoiceOriginalDueAmount = (row: Record<string, any>): number =>
  parseAllocationNumber(
    row.due_amount_original ??
      row.txt_due_amount_original ??
      row.txt_due_amount ??
      row.due_amount ??
      0
  );

const invoiceRowKey = (row: Record<string, any>): { id: string; no: string } => ({
  id: String(row.id ?? row.invoice_id ?? "").trim(),
  no: getInvoiceNoFromRow(row),
});

export const findInvoiceRowInGrid = (
  invoiceIdOrNo: string,
  invoiceRows: Record<string, any>[]
): Record<string, any> | null => {
  if (!invoiceIdOrNo) return null;
  const key = String(invoiceIdOrNo).trim();
  return (
    invoiceRows.find((row) => {
      const id = String(row.id ?? row.invoice_id ?? "").trim();
      const no = getInvoiceNoFromRow(row);
      return id === key || no === key;
    }) ?? null
  );
};

export const getPaidAmountForInvoiceSelection = (
  receiptRow: Record<string, any>,
  invoiceRows: Record<string, any>[]
): number => {
  const rawInvoice = resolveInvoiceRawItem(receiptRow);
  if (rawInvoice) {
    const fromOption = parseAllocationNumber(
      rawInvoice.amount_paid ?? rawInvoice.txt_amount_paid
    );
    if (fromOption > 0) return fromOption;
  }
  const invoiceId = getInvoiceIdFromRow(receiptRow);
  const invoiceNo = getInvoiceNoFromRow(receiptRow);
  const gridRow =
    findInvoiceRowInGrid(invoiceId, invoiceRows) ||
    findInvoiceRowInGrid(invoiceNo, invoiceRows);
  if (gridRow) return getInvoiceOriginalPaidAmount(gridRow);
  return 0;
};

export const getReceiptUnregisteredAmount = (row: Record<string, any>): number =>
  parseAllocationNumber(
    row.unregistered_amount_original ??
      row.txt_unregistered_amount ??
      row.unregistered_amount ??
      0
  );

export const computeMaxReleaseOnReceipt = (
  receiptRow: Record<string, any>,
  invoiceRows: Record<string, any>[],
  options?: {
    receiptRows?: Record<string, any>[];
    rowIndex?: number;
  }
): number => {
  const paid = getPaidAmountForInvoiceSelection(receiptRow, invoiceRows);
  const unreg = getReceiptUnregisteredAmount(receiptRow);
  let allocatedElsewhere = 0;
  const ledgerId = String(receiptRow.id ?? receiptRow.ledger_unregistered_id ?? "");
  if (ledgerId && options?.receiptRows) {
    options.receiptRows.forEach((r, idx) => {
      if (options.rowIndex !== undefined && idx === options.rowIndex) return;
      const rid = String(r.id ?? r.ledger_unregistered_id ?? "");
      if (rid !== ledgerId) return;
      allocatedElsewhere += parseAllocationNumber(
        r[BILL_WISE_RELEASE_ALLOCATE_FIELD] ?? r.amount
      );
    });
  }
  const unregAvailable = roundMoney(Math.max(0, unreg - allocatedElsewhere));
  if (paid <= 0 && unregAvailable <= 0) return 0;
  if (paid <= 0) return unregAvailable;
  if (unregAvailable <= 0) return paid;
  return roundMoney(Math.min(paid, unregAvailable));
};

/** Clear invoice selection and allocation on a receipt row. */
export const clearBillWiseReleaseInvoiceSelection = (
  row: Record<string, any>
): Record<string, any> => {
  const next = { ...row };
  next[BILL_WISE_RELEASE_INVOICE_FIELD] = "";
  next.invoice_id = "";
  next.txt_invoice_no = "";
  next.invoice_no = "";
  next[BILL_WISE_RELEASE_ALLOCATE_FIELD] = 0;
  next.amount = 0;
  next.txt_max_allocate = 0;
  next.txt_amount_paid_ref = 0;
  delete next[`_${BILL_WISE_RELEASE_INVOICE_FIELD}_selectedOption`];
  return next;
};

export const applyInvoiceSelectionToReceiptRow = (
  row: Record<string, any>,
  rawInvoice: Record<string, any> | null,
  invoiceRows: Record<string, any>[],
  options?: { receiptRows?: Record<string, any>[]; rowIndex?: number }
): Record<string, any> => {
  const next = { ...row };
  if (!rawInvoice) return next;

  const invoiceId = rawInvoice.id ?? rawInvoice.value ?? "";
  const invoiceNo =
    rawInvoice.invoice_no ?? rawInvoice.txt_invoice_no ?? "";
  const paid = parseAllocationNumber(
    rawInvoice.amount_paid ?? rawInvoice.txt_amount_paid
  );
  const gridRow = findInvoiceRowInGrid(String(invoiceId), invoiceRows);
  const paidFromGrid = gridRow ? getInvoiceOriginalPaidAmount(gridRow) : 0;
  const resolvedPaid = paidFromGrid > 0 ? paidFromGrid : paid > 0 ? paid : 0;

  next[BILL_WISE_RELEASE_INVOICE_FIELD] = invoiceId ? String(invoiceId) : "";
  next.invoice_id = invoiceId ? String(invoiceId) : "";
  next.txt_invoice_no = invoiceNo ? String(invoiceNo) : "";
  next.invoice_no = invoiceNo ? String(invoiceNo) : "";
  next.txt_amount_paid_ref = resolvedPaid;

  const maxRelease = computeMaxReleaseOnReceipt(
    { ...next, unregistered_amount_original: getReceiptUnregisteredAmount(next) },
    invoiceRows,
    options
  );
  const allocate =
    resolvedPaid > 0
      ? roundMoney(Math.min(resolvedPaid, maxRelease > 0 ? maxRelease : resolvedPaid))
      : maxRelease > 0
        ? maxRelease
        : 0;

  next[BILL_WISE_RELEASE_ALLOCATE_FIELD] = allocate;
  next.amount = allocate;
  next.txt_max_allocate = maxRelease;
  return next;
};

export const capAllocateAmountOnReceiptRow = (
  row: Record<string, any>,
  invoiceRows: Record<string, any>[],
  options?: { receiptRows?: Record<string, any>[]; rowIndex?: number }
): Record<string, any> => {
  const next = { ...row };
  const maxRelease = computeMaxReleaseOnReceipt(next, invoiceRows, options);
  let amount = parseAllocationNumber(
    next[BILL_WISE_RELEASE_ALLOCATE_FIELD] ?? next.amount
  );
  if (maxRelease > 0) {
    amount = roundMoney(Math.min(Math.max(0, amount), maxRelease));
  } else {
    amount = roundMoney(Math.max(0, amount));
  }
  next[BILL_WISE_RELEASE_ALLOCATE_FIELD] = amount;
  next.amount = amount;
  next.txt_max_allocate = maxRelease;
  return next;
};

export const recalcBillWiseReleaseReceiptGrid = (
  receiptRows: Record<string, any>[],
  invoiceRows: Record<string, any>[],
  options?: { changedRowIndex?: number }
): Record<string, any>[] => {
  const rows = receiptRows.map((r) => ({ ...r }));
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    if (
      options?.changedRowIndex !== undefined &&
      options.changedRowIndex !== rowIndex &&
      !getInvoiceIdFromRow(rows[rowIndex]) &&
      !parseAllocationNumber(rows[rowIndex][BILL_WISE_RELEASE_ALLOCATE_FIELD])
    ) {
      continue;
    }
    const rawInvoice = resolveInvoiceRawItem(rows[rowIndex]);
    if (rawInvoice) {
      rows[rowIndex] = applyInvoiceSelectionToReceiptRow(
        rows[rowIndex],
        rawInvoice,
        invoiceRows,
        { receiptRows: rows, rowIndex }
      );
    } else if (
      getInvoiceIdFromRow(rows[rowIndex]) ||
      parseAllocationNumber(rows[rowIndex][BILL_WISE_RELEASE_ALLOCATE_FIELD]) > 0
    ) {
      rows[rowIndex] = capAllocateAmountOnReceiptRow(rows[rowIndex], invoiceRows, {
        receiptRows: rows,
        rowIndex,
      });
    }
  }
  return rows;
};

export const isBillWiseReleaseReceiptSubmodule = (config: {
  name?: string;
  billWiseReleaseMode?: boolean;
  vendorBillWiseReleaseMode?: boolean;
}): boolean =>
  Boolean(
    config.billWiseReleaseMode ||
      config.vendorBillWiseReleaseMode ||
      config.name === "bill_wise_release_unregistered" ||
      config.name === "vendor_bill_wise_release_unregistered"
  );

export const isBillWiseReleaseInvoiceSubmodule = (config: {
  name?: string;
  BindValue?: string;
  billWiseReleaseInvoiceMode?: boolean;
  vendorBillWiseReleaseInvoiceMode?: boolean;
}): boolean => {
  const bind = (config as { BindValue?: string }).BindValue ?? "";
  return Boolean(
    config.billWiseReleaseInvoiceMode ||
      config.vendorBillWiseReleaseInvoiceMode ||
      (config.name === "invoice_recivebles" &&
        (bind === BILL_WISE_RELEASE_INVOICE_BIND || bind === "")) ||
      (config.name === "bill_wise_release_invoice" &&
        bind === BILL_WISE_RELEASE_INVOICE_BIND) ||
      ((config.name === "purchase_recivebles" ||
        config.name === "vendor_bill_wise_release_purchase") &&
        bind === VENDOR_BILL_WISE_RELEASE_PURCHASE_BIND)
  );
};

/** Max release on an invoice row (paid minus releases on same invoice in other rows). */
export const computeMaxReleaseOnInvoiceRow = (
  row: Record<string, any>,
  options?: {
    invoiceRows?: Record<string, any>[];
    rowIndex?: number;
  }
): number => {
  const paid = getInvoiceOriginalPaidAmount(row);
  let releasedElsewhere = 0;
  const invoiceId = getInvoiceIdFromRow(row);
  const invoiceNo = getInvoiceNoFromRow(row);
  if (options?.invoiceRows) {
    options.invoiceRows.forEach((r, idx) => {
      if (options.rowIndex !== undefined && idx === options.rowIndex) return;
      const rid = getInvoiceIdFromRow(r);
      const rno = getInvoiceNoFromRow(r);
      if (
        (invoiceId && rid === invoiceId) ||
        (invoiceNo && rno === invoiceNo)
      ) {
        releasedElsewhere += parseAllocationNumber(
          r[BILL_WISE_RELEASE_RELEASE_FIELD] ??
            r[BILL_WISE_RELEASE_ALLOCATE_FIELD] ??
            r.amount
        );
      }
    });
  }
  return roundMoney(Math.max(0, paid - releasedElsewhere));
};

/** Default release amount to paid; preserve user-entered partial release when valid. */
export const capReleaseAmountOnInvoiceRow = (
  row: Record<string, any>,
  _options?: {
    invoiceRows?: Record<string, any>[];
    rowIndex?: number;
  }
): Record<string, any> => {
  const next = { ...row };
  const paid = getInvoiceOriginalPaidAmount(next);
  const amount = paid > 0 ? roundMoney(paid) : 0;
  next.txt_amount_paid = amount;
  next.amount_paid = amount;
  const existingRelease = parseAllocationNumber(
    next[BILL_WISE_RELEASE_RELEASE_FIELD] ?? next.amount ?? 0
  );
  const releaseAmount =
    existingRelease > 0 ? Math.min(existingRelease, amount) : amount;
  next[BILL_WISE_RELEASE_RELEASE_FIELD] = releaseAmount;
  next.amount = releaseAmount;
  return next;
};

export const recalcBillWiseReleaseInvoiceGrid = (
  invoiceRows: Record<string, any>[],
  options?: { changedRowIndex?: number }
): Record<string, any>[] => {
  const stamped = stampInvoiceOriginalAmounts(invoiceRows.map((r) => ({ ...r })));
  return stamped.map((row, rowIndex) =>
    capReleaseAmountOnInvoiceRow(row, {
      invoiceRows: stamped,
      rowIndex,
    })
  );
};

export const resolveBillWiseReleaseDocumentField = (
  config?: { vendorBillWiseReleaseMode?: boolean; BindValue?: string }
): string => {
  if (
    config?.vendorBillWiseReleaseMode ||
    config?.BindValue === VENDOR_BILL_WISE_RELEASE_PURCHASE_BIND
  ) {
    return VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD;
  }
  return BILL_WISE_RELEASE_INVOICE_FIELD;
};

export const BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME =
  "bill_wise_release_payment_sources";
export const VENDOR_BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME =
  "vendor_bill_wise_release_payment_sources";
export const BILL_WISE_RELEASE_INVOICE_SUBMODULE_NAME =
  "bill_wise_release_invoice";

export const isBillWiseReleasePaymentSourcesSubmodule = (config: {
  name?: string;
  billWiseReleaseInvoiceMode?: boolean;
}): boolean =>
  String(config.name ?? "") === BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME &&
  Boolean(config.billWiseReleaseInvoiceMode);

export const isVendorBillWiseReleasePaymentSourcesSubmodule = (config: {
  name?: string;
  vendorBillWiseReleaseInvoiceMode?: boolean;
}): boolean =>
  String(config.name ?? "") === VENDOR_BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME &&
  Boolean(config.vendorBillWiseReleaseInvoiceMode);

const getPaymentSourcePaidAmount = (row: Record<string, any>): number =>
  parseAllocationNumber(
    row.amount_paid_original ??
      row.txt_amount_paid ??
      row.amount_paid ??
      0
  );

/** Stamp per-invoice paid amount from lookup onto payment-source rows. */
export const stampPaymentSourcePaidAmounts = (
  rows: Record<string, any>[]
): Record<string, any>[] =>
  rows.map((row) => {
    const paid = getPaymentSourcePaidAmount(row);
    return {
      ...row,
      amount_paid_original: paid,
      txt_amount_paid_original: paid,
      amount_paid: paid,
      txt_amount_paid: paid,
      allocation_link_id:
        row.allocation_link_id ?? row.link_id ?? row.txt_allocation_link_id ?? "",
      link_id: row.link_id ?? row.allocation_link_id ?? row.txt_allocation_link_id ?? "",
      source_unregistered_id:
        row.source_unregistered_id ?? row.txt_source_unregistered_id ?? "",
      source_voucher_no: row.source_voucher_no ?? row.txt_source_voucher_no ?? "",
      entry_source: row.entry_source ?? row.txt_entry_source ?? "",
      invoice_no: row.invoice_no ?? row.txt_invoice_no ?? "",
    };
  });

/** Cap/default release on invoice payment-source row (CN/receipt per invoice). */
export const capReleaseAmountOnPaymentSourceRow = (
  row: Record<string, any>
): Record<string, any> => {
  const next = { ...row };
  const paid = getPaymentSourcePaidAmount(next);
  const maxRelease = paid > 0 ? roundMoney(paid) : 0;
  const existing = parseAllocationNumber(
    next[BILL_WISE_RELEASE_RELEASE_FIELD] ?? next.amount ?? 0
  );
  const releaseAmount =
    existing > 0 ? roundMoney(Math.min(existing, maxRelease)) : maxRelease;
  next[BILL_WISE_RELEASE_RELEASE_FIELD] = releaseAmount;
  next.amount = releaseAmount;
  next.txt_max_allocate = maxRelease;
  return next;
};

export const recalcBillWiseReleasePaymentSourcesGrid = (
  rows: Record<string, any>[]
): Record<string, any>[] =>
  stampPaymentSourcePaidAmounts(rows).map((row) =>
    capReleaseAmountOnPaymentSourceRow(row)
  );

/** Stamp per-purchase paid amount from lookup onto vendor payment-source rows. */
export const stampVendorPaymentSourcePaidAmounts = (
  rows: Record<string, any>[]
): Record<string, any>[] =>
  rows.map((row) => {
    const paid = getPaymentSourcePaidAmount(row);
    return {
      ...row,
      amount_paid_original: paid,
      txt_amount_paid_original: paid,
      amount_paid: paid,
      txt_amount_paid: paid,
      allocation_link_id:
        row.allocation_link_id ?? row.link_id ?? row.txt_allocation_link_id ?? "",
      link_id: row.link_id ?? row.allocation_link_id ?? row.txt_allocation_link_id ?? "",
      source_unregistered_id:
        row.source_unregistered_id ?? row.txt_source_unregistered_id ?? "",
      source_voucher_no: row.source_voucher_no ?? row.txt_source_voucher_no ?? "",
      entry_source: row.entry_source ?? row.txt_entry_source ?? "",
      purchase_no: row.purchase_no ?? row.txt_purchase_no ?? "",
    };
  });

/** Cap/default release on purchase payment-source row (DN/payment per purchase). */
export const capReleaseAmountOnVendorPaymentSourceRow = (
  row: Record<string, any>
): Record<string, any> => {
  const next = { ...row };
  const paid = getPaymentSourcePaidAmount(next);
  const maxRelease = paid > 0 ? roundMoney(paid) : 0;
  const existing = parseAllocationNumber(
    next[BILL_WISE_RELEASE_RELEASE_FIELD] ?? next.amount ?? 0
  );
  const releaseAmount =
    existing > 0 ? roundMoney(Math.min(existing, maxRelease)) : maxRelease;
  next[BILL_WISE_RELEASE_RELEASE_FIELD] = releaseAmount;
  next.amount = releaseAmount;
  next.txt_max_allocate = maxRelease;
  return next;
};

export const recalcVendorBillWiseReleasePaymentSourcesGrid = (
  rows: Record<string, any>[]
): Record<string, any>[] =>
  stampVendorPaymentSourcePaidAmounts(rows).map((row) =>
    capReleaseAmountOnVendorPaymentSourceRow(row)
  );

/** Pick invoice-grid vs payment-sources recalc based on submodule config. */
export const recalcBillWiseReleaseSubmoduleByConfig = (
  config: {
    name?: string;
    billWiseReleaseInvoiceMode?: boolean;
    vendorBillWiseReleaseInvoiceMode?: boolean;
  },
  rows: Record<string, any>[],
  options?: { changedRowIndex?: number }
): Record<string, any>[] => {
  if (isBillWiseReleasePaymentSourcesSubmodule(config)) {
    return recalcBillWiseReleasePaymentSourcesGrid(rows);
  }
  if (isVendorBillWiseReleasePaymentSourcesSubmodule(config)) {
    return recalcVendorBillWiseReleasePaymentSourcesGrid(rows);
  }
  if (
    isBillWiseReleaseInvoiceSubmodule(config) ||
    config.billWiseReleaseInvoiceMode ||
    config.vendorBillWiseReleaseInvoiceMode
  ) {
    return recalcBillWiseReleaseInvoiceGrid(rows, options);
  }
  return rows;
};

const BILL_WISE_RELEASE_SUBFORM_IDENTIFIERS = new Set([
  BILL_WISE_RELEASE_INVOICE_SUBMODULE_NAME,
  BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME,
  BILL_WISE_RELEASE_INVOICE_BIND,
  BILL_WISE_RELEASE_RECEIPT_BIND,
  "bill_wise_release_payment_sources_split",
  "bill_allocations_invoice",
]);

export const resolveBillWiseReleaseVoucherType = (
  formData: Record<string, unknown>
): string => {
  const selectedData = formData.drp_voucher_type_selectedData as
    | Record<string, unknown>
    | undefined;
  const raw =
    formData.drp_voucher_type ??
    formData.voucher_type ??
    formData.document_type ??
    "";
  const label = String(selectedData?.label ?? selectedData?.name ?? "");
  if (label.toLowerCase().includes("receipt")) return "receipt_voucher";
  if (label.toLowerCase().includes("invoice")) return "sales_invoice";
  const rawStr = String(raw).toLowerCase();
  if (rawStr.includes("receipt")) return "receipt_voucher";
  if (rawStr.includes("invoice") || rawStr === "sales_invoice") {
    return "sales_invoice";
  }
  return String(raw || "sales_invoice");
};

export const isBillWiseReleaseInvoiceMode = (
  formData: Record<string, unknown>
): boolean =>
  resolveBillWiseReleaseVoucherType(formData) === "sales_invoice";

const readBillWiseReleaseSubmoduleRows = (
  formData: Record<string, unknown>,
  keys: string[]
): Record<string, any>[] => {
  for (const key of keys) {
    const value = formData[key];
    if (Array.isArray(value) && value.length > 0) {
      return value as Record<string, any>[];
    }
  }
  return [];
};

const findExistingBillWiseReleaseSubformData = (
  existingItems: any[],
  names: string[],
  tables: string[]
): Record<string, any>[] | undefined => {
  const nameSet = new Set(names.map((value) => value.toLowerCase()));
  const tableSet = new Set(tables.map((value) => value.toLowerCase()));
  const match = existingItems.find((item) => {
    const name = String(item?.name ?? "").toLowerCase();
    const table = String(item?.tablename ?? "").toLowerCase();
    return (
      (nameSet.has(name) || tableSet.has(table)) &&
      Array.isArray(item?.data) &&
      item.data.length > 0
    );
  });
  return match?.data as Record<string, any>[] | undefined;
};

/** Force correct bill wise release subform names/tables for the active voucher type. */
export const buildBillWiseReleaseSubformItems = (
  formData: Record<string, unknown>,
  existingItems: any[]
): any[] => {
  const mandatory = {
    manadatoryResult: { success: true, fields: [] },
  };
  const kept = existingItems.filter((item) => {
    const name = String(item?.name ?? "").toLowerCase();
    const table = String(item?.tablename ?? "").toLowerCase();
    return (
      !BILL_WISE_RELEASE_SUBFORM_IDENTIFIERS.has(name) &&
      !BILL_WISE_RELEASE_SUBFORM_IDENTIFIERS.has(table)
    );
  });

  if (isBillWiseReleaseInvoiceMode(formData)) {
    const existingData = findExistingBillWiseReleaseSubformData(
      existingItems,
      [
        BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME,
        BILL_WISE_RELEASE_INVOICE_SUBMODULE_NAME,
        "bill_allocations_invoice",
      ],
      [
        BILL_WISE_RELEASE_RECEIPT_BIND,
        BILL_WISE_RELEASE_INVOICE_BIND,
        "bill_wise_release_payment_sources_split",
      ]
    );
    const rows =
      existingData?.length
        ? existingData
        : readBillWiseReleaseSubmoduleRows(formData, [
            BILL_WISE_RELEASE_RECEIPT_BIND,
            BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME,
            BILL_WISE_RELEASE_INVOICE_BASE_KEY,
          ]);
    if (rows.length === 0) return existingItems;
    return [
      ...kept,
      {
        name: BILL_WISE_RELEASE_PAYMENT_SOURCES_NAME,
        tablename: BILL_WISE_RELEASE_RECEIPT_BIND,
        data: rows,
        ...mandatory,
      },
    ];
  }

  const existingData = findExistingBillWiseReleaseSubformData(
    existingItems,
    [BILL_WISE_RELEASE_INVOICE_SUBMODULE_NAME, "bill_allocations_invoice"],
    [BILL_WISE_RELEASE_INVOICE_BIND, "bill_wise_release_invoice_split"]
  );
  const rows =
    existingData?.length
      ? existingData
      : readBillWiseReleaseSubmoduleRows(formData, [
          BILL_WISE_RELEASE_INVOICE_BIND,
          BILL_WISE_RELEASE_INVOICE_SUBMODULE_NAME,
          BILL_WISE_RELEASE_INVOICE_BASE_KEY,
        ]);
  if (rows.length === 0) return existingItems;
  return [
    ...kept,
    {
      name: BILL_WISE_RELEASE_INVOICE_SUBMODULE_NAME,
      tablename: BILL_WISE_RELEASE_INVOICE_BIND,
      data: rows,
      ...mandatory,
    },
  ];
};

/** Build save lines: (ledger_id, amount, invoice_no) from receipt grid. */
export const extractBillWiseReleaseSaveLines = (
  receiptRows: Record<string, any>[]
): Array<{ ledgerId: string; amount: number; invoiceNo: string }> => {
  const lines: Array<{ ledgerId: string; amount: number; invoiceNo: string }> =
    [];
  for (const row of receiptRows) {
    const amount = parseAllocationNumber(
      row[BILL_WISE_RELEASE_ALLOCATE_FIELD] ?? row.amount
    );
    if (amount <= 0) continue;
    const ledgerId = String(
      row.id ?? row.ledger_unregistered_id ?? ""
    ).trim();
    const invoiceNo = getInvoiceNoFromRow(row);
    if (!ledgerId || !invoiceNo) continue;
    lines.push({ ledgerId, amount, invoiceNo });
  }
  return lines;
};

export const stampInvoiceOriginalAmounts = (
  rows: Record<string, any>[]
): Record<string, any>[] =>
  rows.map((row) => {
    const paid = getInvoiceOriginalPaidAmount(row);
    const due = getInvoiceOriginalDueAmount(row);
    return {
      ...row,
      amount_paid_original: paid,
      txt_amount_paid_original: paid,
      due_amount_original: due,
      txt_due_amount_original: due,
      amount_paid: paid,
      txt_amount_paid: paid,
      due_amount: due,
      txt_due_amount: due,
    };
  });

/** @deprecated Use stampInvoiceOriginalAmounts */
export const stampInvoicePaidAmounts = stampInvoiceOriginalAmounts;

/** Sum release amounts per invoice id and invoice no from receipt rows. */
export const sumReleaseAllocationsByInvoice = (
  receiptRows: Record<string, any>[]
): { byId: Record<string, number>; byNo: Record<string, number> } => {
  const byId: Record<string, number> = {};
  const byNo: Record<string, number> = {};
  for (const row of receiptRows) {
    const amount = parseAllocationNumber(
      row[BILL_WISE_RELEASE_ALLOCATE_FIELD] ?? row.amount
    );
    if (amount <= 0) continue;
    const id = getInvoiceIdFromRow(row);
    const no = getInvoiceNoFromRow(row);
    if (id) byId[id] = roundMoney((byId[id] || 0) + amount);
    if (no) byNo[no] = roundMoney((byNo[no] || 0) + amount);
  }
  return { byId, byNo };
};

const allocatedForInvoiceRow = (
  row: Record<string, any>,
  allocations: { byId: Record<string, number>; byNo: Record<string, number> }
): number => {
  const { id, no } = invoiceRowKey(row);
  if (id && allocations.byId[id] !== undefined) return allocations.byId[id];
  if (no && allocations.byNo[no] !== undefined) return allocations.byNo[no];
  return 0;
};

/** Preview invoice paid/due after releases from receipt grid. */
export const applyReleasePreviewToInvoiceRows = (
  baseRows: Record<string, any>[],
  receiptRows: Record<string, any>[]
): Record<string, any>[] => {
  const allocations = sumReleaseAllocationsByInvoice(receiptRows);
  return baseRows.map((row) => {
    const origPaid = getInvoiceOriginalPaidAmount(row);
    const origDue = getInvoiceOriginalDueAmount(row);
    const allocated = allocatedForInvoiceRow(row, allocations);
    const paid = roundMoney(Math.max(0, origPaid - allocated));
    const due = roundMoney(origDue + allocated);
    return {
      ...row,
      amount_paid_original: origPaid,
      txt_amount_paid_original: origPaid,
      due_amount_original: origDue,
      txt_due_amount_original: origDue,
      amount_paid: paid,
      txt_amount_paid: paid,
      due_amount: due,
      txt_due_amount: due,
    };
  });
};

/** Refresh top invoice grid when receipt allocations change. */
export const syncBillWiseReleaseInvoicePreview = (
  receiptRows: Record<string, any>[],
  formData: Record<string, any>,
  onFormDataChange?: (name: string, value: any) => void
): void => {
  if (!onFormDataChange) return;
  const baseKeyRows =
    (formData[BILL_WISE_RELEASE_INVOICE_BASE_KEY] as Record<string, any>[]) ||
    [];
  const base =
    baseKeyRows.length > 0
      ? baseKeyRows
      : (formData[BILL_WISE_RELEASE_INVOICE_BIND] as Record<string, any>[]) ||
        [];
  const stampedBase = stampInvoiceOriginalAmounts(base);
  const preview = applyReleasePreviewToInvoiceRows(stampedBase, receiptRows);
  onFormDataChange(BILL_WISE_RELEASE_INVOICE_BIND, preview);
};
